import React, { useState } from 'react';
import {
  decorateMentions,
  mentionNameMap,
  isMentionedMe,
} from '../../lib/message_text';
import ReactionPicker from './ReactionPicker';

const LIKE_TYPES = ['♥️', '😄', '🙁', '😢', '👍'];

function formatTime(createdAt) {
  const d = new Date(createdAt);
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// @context TGW-1769 Slack TTP: アバター/名前クリックでプロフィール（/member/profile/:member_id）へ遷移。
//   member_id を持たないユーザー（退会等）はリンク化せずそのまま表示する。
function ProfileLink({ user, className, children }) {
  if (!user || !user.member_id) {
    return <span className={className}>{children}</span>;
  }
  return (
    <a className={className} href={`/member/profile/${user.member_id}`}>
      {children}
    </a>
  );
}

// @context TGW-1769 1メッセージ行。連続発言（isGrouped）はアバター/名前を省略して時刻のみ表示。
//   削除ボタンは「自分 or owner/manager」のみ表示（BEは正会員全員に許可するがUIで絞り誤削除を防ぐ）。
//   本文はBEの body_url_to_link（エスケープ+リンク化済み）に decorateMentions を重ねて表示する。
export default function MessageItem({
  message,
  currentUser,
  canManage,
  isGrouped,
  threadsEnabled,
  onOpenThread,
  onToggleReaction,
  onDelete,
  onRetry,
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [copied, setCopied] = useState(false);
  const user = message.user || {};

  // @context TGW-1769 Phase2 本文全文（メンション記法含むプレーンテキスト）をコピーする。
  //   HTML化済みの body_url_to_link ではなく raw の body を使う。
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.body || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // クリップボードが使えない環境（非https等）では何もしない
    }
  };
  const isMine = user.id === currentUser.id;
  const isDeleted = !!message.is_deleted;
  const mentionedMe = isMentionedMe(message, currentUser.id);
  const sending = message.status === 'sending';
  const failed = message.status === 'failed';

  // リアクション集計: like_type ごとの件数と自分が押したか
  const reactionSummary = LIKE_TYPES.map((type) => {
    const likes = (message.messages_like_users || []).filter(
      (l) => l.like_type === type,
    );
    return {
      type,
      count: likes.length,
      mine: likes.some((l) => l.user_id === currentUser.id),
    };
  }).filter((r) => r.count > 0);

  const rowClasses = ['slackch-msg'];
  if (mentionedMe) rowClasses.push('slackch-msg--mentioned');
  if (sending) rowClasses.push('slackch-msg--sending');
  if (failed) rowClasses.push('slackch-msg--failed');

  // @context TGW-1853 本文の安定キー @tunagate_id を、閲覧者文脈で解決済みの表示名（message.mentions）
  //   に差し替える。送信者のあだ名は焼き込まれないため、受信者には自分の名前で「自分宛」として表示される。
  const bodyHtml = decorateMentions(
    message.body_url_to_link || message.body || '',
    mentionNameMap(message),
  );

  return (
    <div className={rowClasses.join(' ')}>
      {!isDeleted && (
        <div className="slackch-msg-toolbar">
          <button
            type="button"
            title="リアクション"
            aria-label="リアクション"
            onClick={() => setShowPicker((v) => !v)}
          >
            😊
          </button>
          {threadsEnabled && message.id && (
            <button
              type="button"
              title="スレッドで返信"
              aria-label="スレッドで返信"
              onClick={() => onOpenThread(message)}
            >
              💬
            </button>
          )}
          {message.body && (
            <button
              type="button"
              title="テキストをコピー"
              aria-label="テキストをコピー"
              onClick={handleCopy}
            >
              {copied ? '✅' : '📋'}
            </button>
          )}
          {(isMine || canManage) && message.id && (
            <button
              type="button"
              title="削除"
              aria-label="メッセージを削除"
              onClick={() => onDelete(message)}
            >
              🗑️
            </button>
          )}
        </div>
      )}
      {showPicker && (
        <ReactionPicker
          onPick={(type) => {
            setShowPicker(false);
            onToggleReaction(message, type);
          }}
          onClose={() => setShowPicker(false)}
        />
      )}

      <div className="slackch-msg-gutter">
        {isGrouped ? (
          <span className="slackch-msg-time-hover">
            {formatTime(message.created_at)}
          </span>
        ) : (
          // Slack TTP: アバター/名前クリックでユーザープロフィールへ遷移（member_id が無い場合は非リンク）
          <ProfileLink user={user} className="slackch-msg-avatar-link">
            {user.avatar_thumb ? (
              <img
                className="slackch-msg-avatar"
                src={user.avatar_thumb}
                alt=""
              />
            ) : (
              <span className="slackch-msg-avatar slackch-msg-avatar--placeholder">
                {(user.familyname || '?').charAt(0)}
              </span>
            )}
          </ProfileLink>
        )}
      </div>

      <div className="slackch-msg-content">
        {!isGrouped && (
          <div className="slackch-msg-meta">
            <ProfileLink user={user} className="slackch-msg-name">
              {user.familyname || ''} {user.firstname || ''}
            </ProfileLink>
            <span className="slackch-msg-time">
              {formatTime(message.created_at)}
            </span>
          </div>
        )}
        {isDeleted ? (
          <div className="slackch-msg-body slackch-msg-body--deleted">
            削除されました
          </div>
        ) : (
          <>
            {/* body_url_to_link はBEでエスケープ済みのHTML（既存UIと同じ扱い） */}
            {/* eslint-disable-next-line react/no-danger */}
            <div
              className="slackch-msg-body"
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
            {message.picture_medium || message.picture_url ? (
              <img
                className="slackch-msg-picture"
                src={message.picture_medium || message.picture_url}
                alt=""
              />
            ) : null}
          </>
        )}

        {failed && (
          <button
            type="button"
            className="slackch-msg-retry"
            onClick={() => onRetry(message.client_id)}
          >
            送信に失敗しました。タップで再送
          </button>
        )}

        {reactionSummary.length > 0 && (
          <div className="slackch-msg-reactions">
            {reactionSummary.map((r) => (
              <button
                key={r.type}
                type="button"
                className={`slackch-reaction-chip ${r.mine ? 'slackch-reaction-chip--mine' : ''}`}
                onClick={() => onToggleReaction(message, r.type)}
              >
                {r.type} {r.count}
              </button>
            ))}
          </div>
        )}

        {threadsEnabled && message.reply_count > 0 && (
          <button
            type="button"
            className="slackch-thread-summary"
            onClick={() => onOpenThread(message)}
          >
            <span className="slackch-thread-avatars">
              {(message.thread_participants || []).map((p) =>
                p.avatar_thumb ? (
                  <img key={p.id} src={p.avatar_thumb} alt="" />
                ) : (
                  <span key={p.id} className="slackch-thread-avatar-fallback">
                    👤
                  </span>
                ),
              )}
            </span>
            <span className="slackch-thread-summary-count">
              {message.reply_count}件の返信
            </span>
            {message.last_reply_at && (
              <span className="slackch-thread-summary-time">
                最終 {formatTime(message.last_reply_at)}
              </span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
